import React, { useState } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  VStack
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const { login, logout } = useAuth();

  const STAFF_ROLES = ['owner', 'therapist', 'receptionist'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowResend(false); // reset every login attempt

    try {
      const { user, error } = await login(email, password);

      if (error) {
        if (error.toLowerCase().includes('email not confirmed')) {
          setShowResend(true);
        }
        throw new Error(error);
      }

      if (!STAFF_ROLES.includes(user.role)) {
        await logout();
        throw new Error(
          'This sign-in page is for clinic staff only. Parents should use the parent portal to sign in.'
        );
      }

      toast({
        title: 'Sign In Successful',
        description: 'You have successfully signed in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      if (user.role === 'owner') {
        navigate('/owner-dashboard');
      } else if (user.role === 'therapist') {
        navigate('/therapist-dashboard');
      } else {
        navigate('/receptionist-dashboard');
      }
    } catch (error) {
      toast({
        title: 'Sign In Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) throw error;

      toast({
        title: 'Verification Email Sent',
        description: 'Please check your inbox.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setShowResend(false); // hide the button after sending
    } catch (error) {
      toast({
        title: 'Failed to Send Email',
        description: error.message || 'An error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box p={8} maxWidth="400px" mx="auto" mt={16}>
      <Heading mb={6}>Sign In</Heading>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="flex-start">
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isDisabled={isLoading}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isDisabled={isLoading}
            />
          </FormControl>
          <Button 
            type="submit" 
            colorScheme="teal" 
            size="lg" 
            width="full"
            isLoading={isLoading}
          >
            Sign In
          </Button>

          {showResend && (
            <Button
              variant="link"
              size="sm"
              colorScheme="blue"
              onClick={handleResendVerification}
              isDisabled={!email || isLoading}
            >
              Send Verification Email
            </Button>
          )}
        </VStack>
      </form>
    </Box>
  );
};

export default SignIn;
